import { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import {
    attachCustomerToOrder,
    searchCustomersForAdminOrderCreate,
    type AdminOrderCustomerSearchResult,
} from "../../api/orderApi";
import { formatPhoneInput } from "../../utils/phone";

type Props = {
    orderId: number;
    currentCustomerId?: number | null;
    currentName?: string | null;
    currentEmail?: string | null;
    disabled?: boolean;
    onAttached: () => void;
};

export default function AttachCustomerControl({
    orderId,
    currentCustomerId,
    currentName,
    currentEmail,
    disabled,
    onAttached,
}: Props) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<"search" | "create">("search");
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<AdminOrderCustomerSearchResult[]>([]);
    const [searching, setSearching] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [draft, setDraft] = useState({ name: "", email: "", phone: "" });

    const runSearch = async (value: string) => {
        setQuery(value);
        setError("");
        if (value.trim().length < 2) {
            setResults([]);
            return;
        }
        setSearching(true);
        try {
            setResults(await searchCustomersForAdminOrderCreate(value));
        } catch {
            setResults([]);
        } finally {
            setSearching(false);
        }
    };

    const attach = async (
        payload: { customer_id: number } | { new_customer: { name: string; email: string; phone?: string } }
    ) => {
        setBusy(true);
        setError("");
        try {
            await attachCustomerToOrder(orderId, payload);
            setOpen(false);
            setQuery("");
            setResults([]);
            setDraft({ name: "", email: "", phone: "" });
            onAttached();
        } catch (err: any) {
            setError(err?.response?.data?.error || "Failed to attach customer.");
        } finally {
            setBusy(false);
        }
    };

    if (currentCustomerId) {
        return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Chip size="small" color="success" label="Account linked" />
                <Typography variant="body2" color="text.secondary" noWrap>
                    {currentName || "Customer"}{currentEmail ? ` · ${currentEmail}` : ""}
                </Typography>
            </Stack>
        );
    }

    return (
        <Box sx={{ mb: 2 }}>
            <Alert
                severity="warning"
                sx={{ mb: open ? 1.5 : 0 }}
                action={
                    !open ? (
                        <Button
                            size="small"
                            startIcon={<PersonAddAlt1Icon fontSize="small" />}
                            onClick={() => setOpen(true)}
                            disabled={disabled}
                        >
                            Add customer
                        </Button>
                    ) : undefined
                }
            >
                No customer account is linked. Link one to enable store-credit refunds.
            </Alert>

            {open && (
                <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.default" }}>
                    {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

                    <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                        <Button size="small" variant={mode === "search" ? "contained" : "text"} onClick={() => setMode("search")}>
                            Find existing
                        </Button>
                        <Button size="small" variant={mode === "create" ? "contained" : "text"} onClick={() => setMode("create")}>
                            Create new
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" color="inherit" onClick={() => setOpen(false)} disabled={busy}>
                            Cancel
                        </Button>
                    </Stack>

                    {mode === "search" ? (
                        <>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search by name, email or phone"
                                value={query}
                                onChange={(e) => runSearch(e.target.value)}
                                disabled={busy}
                            />
                            {searching && <Box sx={{ mt: 1 }}><CircularProgress size={18} /></Box>}
                            <Stack spacing={0.75} sx={{ mt: 1 }}>
                                {results.map((c) => (
                                    <Box
                                        key={c.id}
                                        sx={{
                                            display: "flex", alignItems: "center", justifyContent: "space-between",
                                            gap: 1, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "background.paper",
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={700} noWrap>{c.name}</Typography>
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {c.email}{c.phone ? ` · ${c.phone}` : ""}
                                            </Typography>
                                        </Box>
                                        <Button size="small" variant="contained" disabled={busy} onClick={() => attach({ customer_id: c.id })}>
                                            Link
                                        </Button>
                                    </Box>
                                ))}
                            </Stack>
                        </>
                    ) : (
                        <Stack spacing={1.25}>
                            <TextField size="small" label="Name" value={draft.name} disabled={busy}
                                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
                            <TextField size="small" label="Email" type="email" value={draft.email} disabled={busy}
                                onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} />
                            <TextField size="small" label="Phone (optional)" type="tel" inputProps={{ inputMode: "tel" }} value={draft.phone} disabled={busy}
                                onChange={(e) => setDraft((d) => ({ ...d, phone: formatPhoneInput(e.target.value) }))} />
                            <Typography variant="caption" color="text.secondary">
                                A setup link is emailed so the customer can set their password.
                            </Typography>
                            <Button
                                variant="contained"
                                disabled={busy || !draft.email.trim() || !draft.name.trim()}
                                onClick={() => attach({ new_customer: { name: draft.name.trim(), email: draft.email.trim(), phone: draft.phone.trim() || undefined } })}
                            >
                                {busy ? "Linking…" : "Create & link"}
                            </Button>
                        </Stack>
                    )}
                </Box>
            )}
        </Box>
    );
}
